// ----------------------------------------------------------------------------
// SubmissionModal
// ----------------------------------------------------------------------------
// "Secure Municipal Bid Submission Portal" overlay launched from the AI
// proposal output. Walks the vendor through:
//   1. payload review + signatory / email validation
//   2. a 4-second transmission animation with rotating status lines
//   3. a digital receipt with a randomized confirmation hash + timestamp
//
// In production: replace `runTransmissionSimulation()` below with a real
// `POST /api/submissions` request and stream status updates over WebSocket /
// SSE. The receipt data (`hash`, `submittedAt`) should come from the API
// response, not from `Date.now()`.
// ----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Send,
  ShieldCheck,
  CheckCircle2,
  Lock,
  ReceiptText,
  Building2,
  Hash,
  DollarSign,
  User,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { SUBMIT } from "../constants/testIds";

const TRANSMIT_STAGES = [
  "Authenticating vendor credentials with Secure Gateway…",
  "Validating digital signature and compliance hashes…",
  "Uploading 256-bit encrypted proposal package…",
  "Awaiting municipal server confirmation receipt…",
];

// Pseudo-random confirmation hash. Stable per submission attempt.
const buildConfirmationHash = () => {
  const block = Math.floor(10000 + Math.random() * 89999);
  return `TXN-${block}-FL-2026`;
};

const formatAmount = (amt) =>
  Number(amt || 0).toLocaleString("en-US", { minimumFractionDigits: 0 });

// Per-row text color for the transmission stage checklist. Extracted so the
// JSX below can avoid a nested ternary.
const stageRowClass = (idx, currentStage) => {
  if (idx < currentStage) return "text-emerald-700";
  if (idx === currentStage) return "text-slate-800 font-medium";
  return "text-slate-400";
};

// ============================================================================
// Phase subcomponents — extracted from the parent modal so each phase has a
// single focused responsibility and the parent renders as a thin dispatcher.
// ============================================================================

const FormPhase = ({
  bid,
  company,
  amount,
  signatory,
  email,
  setSignatory,
  setEmail,
  onConfirm,
}) => (
  <div className="space-y-5">
    <div className="rounded-md border border-slate-200 bg-slate-50/70 p-4 space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
        Payload Summary
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 size={13} strokeWidth={1.7} className="text-slate-400 flex-shrink-0" />
          <dt className="text-slate-500">Target Agency:</dt>
          <dd className="text-slate-900 font-medium truncate">{bid.agency}</dd>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Hash size={13} strokeWidth={1.7} className="text-slate-400 flex-shrink-0" />
          <dt className="text-slate-500">Solicitation:</dt>
          <dd className="text-slate-900 font-mono text-[12.5px] truncate">{bid.id}</dd>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <DollarSign size={13} strokeWidth={1.7} className="text-slate-400 flex-shrink-0" />
          <dt className="text-slate-500">Proposed:</dt>
          <dd className="text-slate-900 font-semibold">${formatAmount(amount)}</dd>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <User size={13} strokeWidth={1.7} className="text-slate-400 flex-shrink-0" />
          <dt className="text-slate-500">Company:</dt>
          <dd className="text-slate-900 font-medium truncate">{company || "—"}</dd>
        </div>
      </dl>
    </div>

    <div className="space-y-3">
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
          Authorized Signatory Name
        </label>
        <Input
          data-testid={SUBMIT.inputSignatory}
          value={signatory}
          onChange={(e) => setSignatory(e.target.value)}
          placeholder="e.g., Jane Holloway, VP Operations"
          className="h-10 bg-white border-slate-200"
        />
      </div>
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
          Corporate Notification Email
        </label>
        <Input
          data-testid={SUBMIT.inputEmail}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="bids@yourcompany.com"
          className="h-10 bg-white border-slate-200"
        />
      </div>
    </div>

    <button
      type="button"
      data-testid={SUBMIT.confirm}
      onClick={onConfirm}
      className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-sm transition-colors"
    >
      <ShieldCheck size={16} strokeWidth={1.9} />
      Confirm & Transmit Bid
    </button>
    <p className="text-[11px] text-slate-500 text-center -mt-2">
      A digitally signed copy will be archived to your vendor record.
    </p>
  </div>
);

const TransmittingPhase = ({ stage, progress }) => (
  <div data-testid={SUBMIT.progress} className="py-6 space-y-5 animate-fade-in">
    <div className="flex items-center gap-3">
      <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-60 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-indigo-600" />
      </span>
      <p data-testid={SUBMIT.status} className="text-sm font-medium text-slate-800">
        {TRANSMIT_STAGES[stage]}
      </p>
    </div>
    <Progress value={progress} className="h-2 bg-slate-100" />
    <ul className="space-y-1.5 text-xs text-slate-500">
      {TRANSMIT_STAGES.map((s, i) => (
        <li key={s} className={`flex items-center gap-2 ${stageRowClass(i, stage)}`}>
          {i < stage ? (
            <CheckCircle2 size={12} strokeWidth={2} className="text-emerald-600" />
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
          )}
          {s}
        </li>
      ))}
    </ul>
  </div>
);

const SuccessPhase = ({ bid, receipt, copied, onCopyHash, onClose }) => (
  <div data-testid={SUBMIT.success} className="py-3 space-y-5 animate-fade-in text-center">
    <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
      <CheckCircle2 size={36} strokeWidth={1.8} className="text-emerald-600 animate-fade-in" />
    </div>
    <h4 className="font-display text-xl font-semibold text-slate-900">
      Bid Package Successfully Transmitted!
    </h4>

    <div
      data-testid={SUBMIT.receipt}
      className="text-left rounded-lg border border-slate-200 bg-slate-50/60 overflow-hidden"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white">
        <ReceiptText size={14} strokeWidth={1.7} className="text-slate-500" />
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600">
          Digital Receipt
        </p>
        <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
          Pending Agency Review
        </span>
      </div>
      <dl className="px-4 py-3 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-slate-500">Confirmation Hash</dt>
          <dd className="flex items-center gap-2">
            <code className="font-mono text-[12.5px] text-slate-900 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
              {receipt.hash}
            </code>
            <button
              type="button"
              onClick={onCopyHash}
              className="text-slate-500 hover:text-slate-900 transition-colors"
              aria-label="Copy confirmation hash"
            >
              {copied ? (
                <Check size={13} strokeWidth={2} className="text-emerald-600" />
              ) : (
                <Copy size={13} strokeWidth={1.8} />
              )}
            </button>
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-slate-500">Submitted</dt>
          <dd className="text-slate-900 font-medium font-mono text-[12.5px]">
            {receipt.submittedAt.toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "medium",
            })}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-slate-500">Solicitation</dt>
          <dd className="text-slate-900 font-mono text-[12.5px]">{bid.id}</dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-slate-500">Agency</dt>
          <dd className="text-slate-900 font-medium truncate">{bid.agency}</dd>
        </div>
      </dl>
    </div>

    <button
      type="button"
      data-testid={SUBMIT.close}
      onClick={onClose}
      className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-colors"
    >
      Return to Ledger
    </button>
  </div>
);

export const SubmissionModal = ({ open, onClose, bid, company, amount }) => {
  // Phase: 'form' | 'transmitting' | 'success'
  const [phase, setPhase] = useState("form");
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [signatory, setSignatory] = useState("");
  const [email, setEmail] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [copied, setCopied] = useState(false);
  const timers = useRef([]);

  // Reset internal state every time the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setPhase("form");
      setStage(0);
      setProgress(0);
      setSignatory("");
      setEmail("");
      setReceipt(null);
      setCopied(false);
    }
    return () => timers.current.forEach(clearTimeout);
  }, [open]);

  if (!open || !bid) return null;

  // -------------------------------------------------------------------------
  // runTransmissionSimulation — main "send to agency" handler.
  // SWAP HERE for the real API call:
  //   await axios.post('/api/submissions', { bidId: bid.id, company, ... })
  // -------------------------------------------------------------------------
  const runTransmissionSimulation = () => {
    if (!signatory.trim() || !email.trim()) {
      toast.error("Signatory name and email are required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid corporate email");
      return;
    }
    setPhase("transmitting");
    setStage(0);
    setProgress(8);

    // Schedule 4 rotating status messages + smooth progress.
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => { setStage(1); setProgress(32); }, 1000),
      setTimeout(() => { setStage(2); setProgress(60); }, 2000),
      setTimeout(() => { setStage(3); setProgress(86); }, 3000),
      setTimeout(() => {
        setProgress(100);
        setReceipt({
          hash: buildConfirmationHash(),
          submittedAt: new Date(),
        });
        setPhase("success");
      }, 4000),
    ];
  };

  const handleClose = () => {
    if (phase === "transmitting") return; // lock during transmission
    onClose();
  };

  const copyHash = async () => {
    if (!receipt) return;
    try {
      await navigator.clipboard.writeText(receipt.hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close submission modal"
        onClick={handleClose}
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
      />

      {/* Card */}
      <div
        data-testid={SUBMIT.modal}
        className="relative bg-white w-full max-w-xl rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-bold uppercase tracking-[0.15em]">
              <Lock size={11} strokeWidth={2} /> Secure Channel
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Secure Municipal Bid Submission Portal
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={phase === "transmitting"}
            aria-label="Close"
            className="h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X size={16} strokeWidth={1.8} />
          </button>
        </div>

        {/* Body — thin dispatcher over the three phase subcomponents */}
        <div className="px-6 py-5">
          {phase === "form" && (
            <FormPhase
              bid={bid}
              company={company}
              amount={amount}
              signatory={signatory}
              email={email}
              setSignatory={setSignatory}
              setEmail={setEmail}
              onConfirm={runTransmissionSimulation}
            />
          )}
          {phase === "transmitting" && (
            <TransmittingPhase stage={stage} progress={progress} />
          )}
          {phase === "success" && receipt && (
            <SuccessPhase
              bid={bid}
              receipt={receipt}
              copied={copied}
              onCopyHash={copyHash}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};
